<template>
  <div class="w-full bg-theme-600 px-4 py-3 flex items-center justify-between sm:px-6 text-theme">
    <!-- Desktop navigation -->
    <div class="flex-1 justify-between sm:hidden" :class="showNavOnMobile ? 'hidden' : 'flex'">
      <!-- Previous button (hidden if on first page) -->
      <a
        class="relative inline-flex justify-center items-center w-[60px] h-[35px] text-sm border-border-blue font-body rounded-md cursor-pointer"
        :class="page === 1 ? 'hidden':'bg-transparent text-blue'"
        @click="changePage(page-1)"
      >
        {{ $t('previous') }}
      </a>
      <!-- Next button (styled differently if on last page) -->
      <a
        class="ltr:ml-3 rtl:mr-3 relative inline-flex justify-center items-center w-[60px] h-[35px] text-sm border-border-blue font-body rounded-md cursor-pointer"
        :class="page === lastPage ? 'bg-blue text-white':'bg-transparent text-blue'"
        @click="changePage(page+1)"
      >
        {{ $t('next').tTitle() }}
      </a>
    </div>

    <!-- Mobile navigation -->
    <div class="sm:flex-1 sm:flex sm:items-center sm:justify-between" :class="showNavOnMobile ? '' : 'hidden'">
      <div>
        <nav
          class="relative z-0 inline-flex rounded-md select-none"
          aria-label="Pagination"
        >
          <!-- Previous button (hidden if on first page) -->
          <a
            class="relative inline-flex justify-center items-center w-[35px] h-[35px] text-blue border border-blue rounded-md m-1 text-sm font-body cursor-pointer"
            :class="{'hidden': page === 1}"
            @click="changePage(page-1)"
          >
            <span class="sr-only text-blue">{{ $t('previous') }}</span>
            <img
              width="9px"
              src="../../src/assets/img/icons/arrow-back-circle-outline.svg"
            >
          </a>

          <!-- Page numbers -->
          <a
            v-for="(p, i) in pageList"
            :key="`pagination-number-list-${i}`"
            aria-current="page"
            class="relative inline-flex justify-center items-center w-[35px] h-[35px] border border-blue rounded-md m-1 text-sm font-body cursor-pointer"
            :class="page === p ? 'z-10 bg-blue text-white':'bg-transparent text-blue'"
            @click="changePage(p)"
          >
            {{ p }}
          </a>

          <!-- Next button (hidden if on last page) -->
          <a
            class="relative inline-flex justify-center items-center w-[35px] h-[35px] text-blue border border-blue rounded-md m-1 text-sm font-body cursor-pointer"
            :class="{'hidden': page === lastPage}"
            @click="changePage(page+1)"
          >
            <span class="sr-only">{{ $t('next').tTitle() }}</span>
            <img
              width="9px"
              src="../../src/assets/img/icons/arrow-forward-circle-outline.svg"
            >
          </a>
        </nav>
      </div>
    </div>
  </div>
</template>



<script>
export default {
  props: {
    page: Number, // current page number
    maxPerPage: Number, // maximum items per page
    total: Number, // total number of items
    showNavOnMobile: { // toggle for mobile navigation
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      delta: 2 // range of pages to show around the current page for desktop
    }
  },
  computed: {
    showing() {
      // calculates first and last item currently being shown
      const from = (this.page - 1) * this.maxPerPage + 1
      let to = (this.page - 1) * this.maxPerPage + this.maxPerPage
      if (to > this.total) {
        to = this.total
      }
      return { from, to }
    },
    lastPage() {
      // determines the total number of pages
      return Math.ceil(this.total / this.maxPerPage)
    },
    pageList() {
      // checks if current view is mobile based on screen width
      const isMobile = window.innerWidth <= 600;

      if (isMobile) {
        // number of visible pages changes depending on narrower screen
        let nrPages = window.innerWidth < 400 ? 4 : 5

        // if total pages are less/equal than max pages, display them all
        if (this.lastPage <= nrPages) {
          return Array(this.lastPage).fill().map((_, index) => index + 1);
        }

        // beginning of pagination
        if (this.page <= nrPages - 1) {
          return window.innerWidth < 400
            ? [1, 2, 3, '...', this.lastPage]
            : [1, 2, 3, 4, '...', this.lastPage];
        }
        // near the end of pagination
        else if (this.page >= this.lastPage - 2) {
          return [1, '...', this.lastPage - 2, this.lastPage - 1, this.lastPage];
        }
        // somewhere in the middle of pagination
        else {
          return [1, '...', this.page, '...', this.lastPage];
        }
      }
      // Desktop pagination logic
      else {
        const delta = this.delta;
        const range = Array(this.lastPage).fill().map((_, index) => index + 1);

        // builds a range of pages, with ellipses where appropriate
        return range.reduce((pages, page) => {
          if (page === 1 || page === this.lastPage) {
            return [...pages, page];
          }
          if (page - delta <= this.page && page + delta >= this.page) {
            return [...pages, page];
          }
          if (pages[pages.length - 1] !== '...') {
            return [...pages, '...'];
          }
          return pages;
        }, []);
      }
    }
  },
  methods: {
    changePage(page) {
      // prevents invalid page input (non-integers, out of range)
      if (!Number.isInteger(page)) {
        return
      }
      if (page < 1 || page > this.lastPage) {
        return
      }

      // emits event when page changes
      this.$emit('update:page', page)
      if(this.$bus){
        this.$bus.$emit('pageChanged', page)
      }
    },
  },
}
</script>
